import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { debounce } from 'lodash'

export interface MemeOptions {
    HTMLAttributes: Record<string, any>
    memeClass?: string
    onMemeClick?: (memeContent: string, event: Event, element: HTMLElement) => void
}

export const MemePluginKey = new PluginKey('meme')

export const MemeExtension = Extension.create<MemeOptions>({
    name: 'meme',

    addOptions() {
        return {
            HTMLAttributes: {},
        }
    },

    addProseMirrorPlugins() {
        const { memeClass, onMemeClick } = this.options

        return [
            new Plugin({
                key: MemePluginKey,

                view(editorView) {
                    const processMemes = () => {
                        const { state, dispatch } = editorView
                        const { doc, tr } = state

                        let transaction = tr
                        let hasChanges = false

                        // Find all meme patterns in the document and existing meme links
                        doc.descendants((node, pos) => {
                            if (node.isText && node.text) {
                                const text = node.text
                                const memeRegex = /\[\[\s*([\w\s]+)\s*\]\]/g
                                let match

                                const matches = []
                                while ((match = memeRegex.exec(text)) !== null) {
                                    matches.push({
                                        start: pos + match.index,
                                        end: pos + match.index + match[0].length,
                                        content: match[1].trim(),
                                        fullMatch: match[0],
                                    })
                                }

                                // Process matches in reverse order to maintain positions
                                matches.reverse().forEach(memeMatch => {
                                    const { start, end, content } = memeMatch

                                    // Convert [[content]] to: [[ + link(content with @v2quest_self_ prefix) + ]]
                                    const linkAttrs: { href: string; class?: string } = {
                                        href: `@v2quest_self_${content}`,
                                    }
                                    if (memeClass) {
                                        linkAttrs.class = memeClass
                                    }

                                    transaction = transaction
                                        .delete(start, end) // Remove original [[content]]
                                        .insertText('[[', start) // Insert opening brackets
                                        .insertText(content, start + 2) // Insert content
                                        .insertText(']]', start + 2 + content.length) // Insert closing brackets
                                        .addMark(
                                            start + 2,
                                            start + 2 + content.length, // Apply mark only to content
                                            state.schema.marks.link.create(linkAttrs),
                                        )

                                    hasChanges = true
                                })
                            }

                            // Check for existing links that should be styled as memes
                            if (node.marks) {
                                node.marks.forEach(mark => {
                                    if (mark.type.name === 'link' && mark.attrs.href) {
                                        const href = mark.attrs.href

                                        // Check if this is a meme link (with @v2quest_self_ prefix)
                                        if (href.startsWith('@v2quest_self_')) {
                                            // Apply meme styling if not already present
                                            const linkAttrs: { href: string; class?: string } = {
                                                href,
                                            }
                                            if (memeClass) {
                                                linkAttrs.class = memeClass
                                            }

                                            // Only update if the class is missing or different
                                            if (
                                                !mark.attrs.class ||
                                                !mark.attrs.class.includes(memeClass)
                                            ) {
                                                transaction = transaction
                                                    .removeMark(pos, pos + node.nodeSize, mark)
                                                    .addMark(
                                                        pos,
                                                        pos + node.nodeSize,
                                                        state.schema.marks.link.create(linkAttrs),
                                                    )

                                                hasChanges = true
                                            }
                                        }
                                    }
                                })
                            }
                        })

                        if (hasChanges) {
                            dispatch(transaction)
                        }
                    }

                    // Process existing memes on initial load
                    setTimeout(() => {
                        processMemes()
                    }, 100)

                    // Debounce the processing to avoid excessive updates
                    let timeout: number

                    return {
                        update: (view, prevState) => {
                            // Only process if the document actually changed
                            if (view.state.doc.eq(prevState.doc)) {
                                return
                            }

                            clearTimeout(timeout)
                            timeout = setTimeout(() => {
                                processMemes()
                            }, 100) // Small delay to batch changes
                        },

                        destroy: () => {
                            clearTimeout(timeout)
                        },
                    }
                },
            }),

            // Meme click handler plugin - using DOM events for better compatibility
            new Plugin({
                key: new PluginKey('memeClickHandler'),

                view(editorView) {
                    const handleMemeClick = (event: Event) => {
                        const target = event.target as HTMLElement

                        // Check if the clicked element is a link
                        if (target && target.tagName === 'A') {
                            const href = target.getAttribute('href')
                            const className = target.getAttribute('class') || ''

                            // Check if this is a meme link
                            const isMeme =
                                href &&
                                (href.startsWith('@v2quest_self_') ||
                                    (memeClass && className.includes(memeClass)))

                            if (isMeme) {
                                event.preventDefault()
                                event.stopPropagation()

                                // Extract meme content - remove @v2quest_self_ prefix if present
                                let memeContent = href || ''
                                if (memeContent.startsWith('@v2quest_self_')) {
                                    memeContent = memeContent.replace('@v2quest_self_', '')
                                }

                                // Call the callback directly instead of dispatching events
                                if (onMemeClick) {
                                    onMemeClick(memeContent, event, target)
                                }

                                return true
                            }
                        }

                        return false
                    }

                    // Add click listener to the editor DOM
                    const editorDOM = editorView.dom
                    editorDOM.addEventListener('click', handleMemeClick, true) // Use capture phase

                    return {
                        destroy() {
                            editorDOM.removeEventListener('click', handleMemeClick, true)
                        },
                    }
                },
            }),
        ]
    },
})
